// Pure Screensaver model — every recurring card is ONE OBLIGATION resolved through the single
// recurrence engine (getOccurrencesOnIso). Identity travels with the card:
//   _occKey (SOURCE iso) · _displayIso · time (effective) · _movedFrom
// Nothing here derives identity from "the task's next occurrence".
import { getOccurrencesOnIso, isOccurrenceSnoozed, effectiveOverrides, getMissedOccurrences, isTaskSnoozed, todayStr, daysDiff, overdueEditTarget } from "./utils";

export const WINDOW_IDS = ["completed", "older", "overdue", "today", "tomorrow", "week", "later", "someday"];

export const isoAtOffset = (offset, now = new Date()) => {
    const d = new Date(now); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Days from `now`'s calendar day to `iso` (negative = past). Pure counterpart of utils.daysDiff. */
export const dayOffsetFrom = (iso, now = new Date()) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).split("-").map(Number);
    if (!y || !m || !d) return null;
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return Math.round((new Date(y, m - 1, d) - start) / 86_400_000);
};

const hhmmMs = (time, now) => {
    const [hh, mm] = String(time).split(":").map(Number);
    const d = new Date(now); d.setHours(hh || 0, mm || 0, 0, 0);
    return d.getTime();
};

/** Bucket every task into the Screensaver windows. Pure: `now` is injected, nothing is read from
 *  the wall clock. Recurring rows are expanded ONE CARD PER OPEN OBLIGATION (identity attached);
 *  Overdue keeps one grouped row per series with its missed SOURCE keys. */
export const groupTasks = (tasks, now = new Date()) => {
    const nowMs = now.getTime();
    const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
    const groups = { completed: [], older: [], overdue: [], today: [], tomorrow: [], week: [], later: [], someday: [] };
    for (const t of (tasks || []).filter((x) => x && !x.archived && !isTaskSnoozed(x, nowMs))) {
        // A malformed recurring row (interval=null) is a stuck one-shot: completed really means done.
        if (t.completed && (t.type !== "recurring" || !t.interval)) { groups.completed.push(t); continue; }
        if (t.type === "recurring" && t.interval) {
            const misses = getMissedOccurrences(t, now);
            const overdueKeys = new Set(misses);
            if (misses.length) groups.overdue.push({ ...t, _missedOccurrences: misses, _missedCount: misses.length, _oldestMissedIso: misses[0] });
            // Legacy gate: a timed series checked in via completed_at after today's cycle boundary
            // (its scheduled time, not midnight) hides only today's NATIVE beat — never a moved one.
            const boundary = t.time ? hhmmMs(t.time, now) : startOfToday.getTime();
            const legacyGate = (t.completed_at || 0) >= boundary;
            let placed = false;
            for (let i = 0; i <= 30; i++) {
                // ONE LOCATION PER OBLIGATION: a today beat already counted as missed stays in Overdue only.
                const occs = openOccurrencesOn(t, isoAtOffset(i, now), now, i === 0 && legacyGate).filter((o) => !overdueKeys.has(o.key));
                if (!occs.length) continue;
                const bucket = i === 0 ? groups.today : i === 1 ? groups.tomorrow : i <= 7 ? groups.week : groups.later;
                for (const occ of occs) bucket.push(occurrenceCard(t, occ, i));
                placed = true;
            }
            if (!placed) groups.someday.push(t);
            continue;
        }
        const d = dayOffsetFrom(t.date, now);
        if (d === null) { groups.someday.push(t); continue; }
        // A timed task earlier today is overdue (one location — not on Today's focus screen too).
        // A date-only task is Today for its whole local day (d === 0) and overdue only from d < 0.
        if (d === 0 && t.time && hhmmMs(t.time, now) <= nowMs) { groups.overdue.push(t); continue; }
        if (d < -30) groups.older.push(t);
        else if (d < 0) groups.overdue.push(t);
        else if (d === 0) groups.today.push(t);
        else if (d === 1) groups.tomorrow.push(t);
        else if (d <= 7) groups.week.push(t);
        else if (d <= 30) groups.later.push(t);
        else groups.someday.push(t);
    }
    // Effective date+time ascending: a card's `_displayIso` (moved beat) beats the series anchor.
    const effIso = (c) => c._displayIso || c._occurrenceIso || c.date || null;
    for (const k of Object.keys(groups)) {
        groups[k].sort((a, b) => {
            const da = effIso(a) ? dayOffsetFrom(effIso(a), now) : 999;
            const db = effIso(b) ? dayOffsetFrom(effIso(b), now) : 999;
            if (da !== db) return da - db;
            return (a.time || "23:59").localeCompare(b.time || "23:59");
        });
    }
    groups.completed.sort((a, b) => (b.completed_at || 0) - (a.completed_at || 0));
    groups.older.sort((a, b) => (dayOffsetFrom(a.date, now) || 0) - (dayOffsetFrom(b.date, now) || 0));
    return groups;
};

/** The raw card list a window shows before per-day expansion (This Week / Later are supersets). */
export const windowBucket = (groups, windowId) => {
    if (windowId === "week") return [...(groups.today || []), ...(groups.tomorrow || []), ...(groups.week || [])];
    if (windowId === "later") return [...(groups.today || []), ...(groups.tomorrow || []), ...(groups.week || []), ...(groups.later || [])];
    return groups[windowId] || [];
};

/** Cards the Screensaver cycles through for `windowId`, in swipe order. Aggregated windows are
 *  expanded to one card per obligation per day; single windows are timed-first. */
export const windowCards = (groups, windowId, now = new Date()) => {
    const combined = windowBucket(groups, windowId);
    if (windowId === "week" || windowId === "later") return expandWindowPerObligation(combined, windowId === "week" ? 7 : 30, now);
    return combined.slice().sort((a, b) => {
        const aHas = a.time ? 0 : 1; const bHas = b.time ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        return (a.time || "").localeCompare(b.time || "");
    });
};

/** Open obligations of a recurring task landing on display day `iso` (completion/snooze judged by SOURCE key). */
export const openOccurrencesOn = (task, iso, now = new Date(), legacyGate = false) =>
    getOccurrencesOnIso(task, iso).filter((occ) => {
        if ((task.completions || []).includes(occ.key)) return false;
        if (isOccurrenceSnoozed(task, occ.key, now.getTime())) return false;
        return occ.movedFrom ? true : !legacyGate;
    });

export const occurrenceCard = (task, occ, offset) => ({
    ...task,
    time: occ.time,
    _occKey: occ.key,
    _displayIso: occ.displayIso,
    _movedFrom: occ.movedFrom,
    _occurrenceOffset: offset,
    _occurrenceIso: occ.displayIso,
    _occurrenceTime: occ.time || null,
});

/** Aggregated This Week / Later windows: one card PER OBLIGATION per day, timed first within a day. */
export const expandWindowPerObligation = (combined, spanDays, now = new Date()) => {
    const nowMs = now.getTime();
    const uniq = new Map();
    for (const t of combined) {
        if (!t || uniq.has(t.id) || t.archived) continue;
        if (t.snoozed_until && t.snoozed_until >= nowMs) continue;
        if (t.completed && t.type !== "recurring") continue;
        uniq.set(t.id, t);
    }
    const perDay = [];
    for (let d = 0; d < spanDays; d++) {
        const iso = isoAtOffset(d, now);
        const bucket = [];
        for (const t of uniq.values()) {
            if (t.type === "recurring" && t.interval) {
                for (const occ of openOccurrencesOn(t, iso, now)) bucket.push(occurrenceCard(t, occ, d));
            } else if (t.date === iso) {
                bucket.push({ ...t, _occurrenceOffset: d, _occurrenceIso: iso, _occurrenceTime: t.time || null });
            }
        }
        bucket.sort((a, b) => {
            const at = a._occurrenceTime || ""; const bt = b._occurrenceTime || "";
            if (!!at !== !!bt) return at ? -1 : 1;
            return at.localeCompare(bt);
        });
        perDay.push(...bucket);
    }
    return perDay;
};

/** DayStrip cell count = open obligations landing that day (not unique task ids). */
export const countOpenObligationsOn = (tasks, iso, now = new Date()) => {
    const nowMs = now.getTime();
    const seen = new Set();
    let n = 0;
    for (const t of tasks) {
        if (!t || seen.has(t.id)) continue;
        seen.add(t.id);
        if (t.archived) continue;
        if (t.snoozed_until && t.snoozed_until >= nowMs) continue;
        if (t.completed && t.type !== "recurring") continue;
        if (t.type === "recurring" && t.interval) n += openOccurrencesOn(t, iso, now).length;
        else if (t.date === iso) n += 1;
    }
    return n;
};

/** Which day (offset from today) a card belongs to — used for the DayStrip highlight only. */
export const cardOffset = (card) => {
    if (!card) return null;
    if (card._occurrenceOffset != null) return card._occurrenceOffset;
    if (card._displayIso) return daysDiff(card._displayIso);
    return card.date ? daysDiff(card.date) : null;
};

/** Double-tap → the exact obligation to edit. Never guesses; 2+ outstanding misses need a pick. */
export const doubleTapTarget = (card, now = new Date()) => {
    if (!card) return undefined;
    if (card.type !== "recurring" || !card.interval) return { ...card, _occurrenceDate: card.date || todayStr() };
    if (card._occKey) return { ...card, _occurrenceDate: card._displayIso || card._occKey };
    if (card._missedOccurrences || card._missedCount) {
        // Overdue card: same decision as the Overdue list row (0 → none · 1 → exact key · 2+ → pick).
        const r = overdueEditTarget(card, now);
        return r.kind === "none" ? { ...card, _noOccurrence: true } : r.task;
    }
    for (let off = 0; off < 400; off++) {
        const iso = isoAtOffset(off, now);
        const occs = openOccurrencesOn(card, iso, now);
        if (occs.length === 1) return { ...card, _occKey: occs[0].key, _displayIso: iso, _occurrenceDate: iso };
        if (occs.length > 1) return { ...card, _needsOccurrencePick: true, _pickIso: iso, _pickKeys: occs.map((o) => o.key) };
    }
    return { ...card, _occurrenceDate: todayStr() };
};
